import {
    type components,
    type JoinedOutreachRead,
} from '@libraries/typescript/api_client';

type ShiftForSelectionOrdering = Pick<
    JoinedOutreachRead['shifts'][number],
    'id' | 'shift_start_time'
>;

type ShiftGroupForSelectionWarning = {
    readonly id?: string;
    readonly shiftIds?: ReadonlyArray<string>;
    readonly shift_ids?: ReadonlyArray<string>;
    readonly groupMode?: 'all' | 'any';
    readonly group_mode?: 'all' | 'any';
};

export type CaregiverShiftIds =
    components['schemas']['StartOutreachRequest']['caregiver_shift_ids'];

export type PartialGroupSelectionWarning = {
    readonly caregiverId: string;
    readonly groupId: string;
    readonly groupIndex: number;
    readonly selectedShiftIds: Array<string>;
    readonly missingShiftIds: Array<string>;
};

export function normalizeSelectedCaregiverIds(
    caregiverIds: Array<string>,
): Array<string> {
    return Array.from(new Set(caregiverIds)).sort();
}

export function withoutCaregiversInSelection(
    selectedCaregiverIdsByShift: Readonly<Record<string, Array<string>>>,
    caregiverIds: ReadonlyArray<string>,
): Record<string, Array<string>> {
    return Object.fromEntries(
        Object.entries(selectedCaregiverIdsByShift).map(
            ([shiftId, selectedIds]) => [
                shiftId,
                selectedIds.filter((id) => !caregiverIds.includes(id)),
            ],
        ),
    );
}

/**
 * Order-insensitive equality of two caregiver→shift-ids mappings. Used to
 * decide whether the outreach table's live selection still matches a
 * proposed start (same caregivers, same shifts per caregiver).
 */
export function caregiverShiftIdsEqual(
    left: CaregiverShiftIds,
    right: CaregiverShiftIds,
): boolean {
    const leftCaregiverIds = Object.keys(left).sort();
    const rightCaregiverIds = Object.keys(right).sort();
    if (
        leftCaregiverIds.length !== rightCaregiverIds.length ||
        leftCaregiverIds.some(
            (caregiverId, index) => caregiverId !== rightCaregiverIds[index],
        )
    ) {
        return false;
    }
    return leftCaregiverIds.every((caregiverId) => {
        const leftShiftIds = [...(left[caregiverId] ?? [])].sort();
        const rightShiftIds = [...(right[caregiverId] ?? [])].sort();
        return (
            leftShiftIds.length === rightShiftIds.length &&
            leftShiftIds.every(
                (shiftId, index) => shiftId === rightShiftIds[index],
            )
        );
    });
}

function orderKnownShiftIds(
    shiftIds: ReadonlyArray<string>,
    shifts: ReadonlyArray<ShiftForSelectionOrdering>,
): Array<string> {
    const shiftById = new Map(shifts.map((shift) => [shift.id, shift]));
    const knownShiftIds = shiftIds.filter((shiftId) => shiftById.has(shiftId));
    const unknownShiftIds = shiftIds.filter(
        (shiftId) => !shiftById.has(shiftId),
    );

    knownShiftIds.sort((leftShiftId, rightShiftId) => {
        const leftShift = shiftById.get(leftShiftId);
        const rightShift = shiftById.get(rightShiftId);

        if (!leftShift || !rightShift) {
            return leftShiftId.localeCompare(rightShiftId);
        }
        if (leftShift.shift_start_time === rightShift.shift_start_time) {
            return leftShift.id.localeCompare(rightShift.id);
        }
        return leftShift.shift_start_time.localeCompare(
            rightShift.shift_start_time,
        );
    });

    return [...knownShiftIds, ...unknownShiftIds];
}

function normalizeShiftGroupsForSelectionWarnings(
    shiftGroups: ReadonlyArray<ShiftGroupForSelectionWarning> | undefined,
    shifts: ReadonlyArray<ShiftForSelectionOrdering>,
): Array<{
    readonly id: string;
    readonly groupIndex: number;
    readonly shiftIds: Array<string>;
}> {
    return (shiftGroups ?? []).flatMap((shiftGroup, groupIndex) => {
        const groupMode =
            shiftGroup.groupMode ?? shiftGroup.group_mode ?? 'all';
        if (groupMode !== 'all') {
            return [];
        }

        const shiftIds = orderKnownShiftIds(
            Array.from(
                new Set(shiftGroup.shiftIds ?? shiftGroup.shift_ids ?? []),
            ),
            shifts,
        );
        if (shiftIds.length <= 1) {
            return [];
        }

        return [
            {
                id: shiftGroup.id ?? `group-${groupIndex + 1}`,
                groupIndex,
                shiftIds,
            },
        ];
    });
}

/**
 * Convert shift-centric selections into caregiver-centric mapping.
 *
 * Takes a record keyed by shift ID whose values are caregiver ID
 * arrays, merges the current tab's selection, and returns a record
 * keyed by caregiver ID whose values are shift ID arrays — the
 * shape expected by StartOutreachRequest.caregiver_shift_ids.
 */
export function buildCaregiverShiftIdsFromSelections({
    selectedCaregiverIdsByShift,
    shifts,
    currentShiftId,
    currentCaregiverIds,
}: {
    readonly selectedCaregiverIdsByShift: Readonly<
        Record<string, Array<string>>
    >;
    readonly shifts: JoinedOutreachRead['shifts'];
    readonly currentShiftId: string;
    readonly currentCaregiverIds: Array<string>;
}): CaregiverShiftIds {
    const selectedShiftIdsByCaregiver = new Map<string, Set<string>>();
    const currentSelectionByShift = {
        ...selectedCaregiverIdsByShift,
        [currentShiftId]: normalizeSelectedCaregiverIds(currentCaregiverIds),
    };

    for (const shift of shifts) {
        const caregiverIds = currentSelectionByShift[shift.id] ?? [];
        for (const caregiverId of caregiverIds) {
            const shiftIds =
                selectedShiftIdsByCaregiver.get(caregiverId) ?? new Set();
            shiftIds.add(shift.id);
            selectedShiftIdsByCaregiver.set(caregiverId, shiftIds);
        }
    }

    return Object.fromEntries(
        Array.from(selectedShiftIdsByCaregiver.entries()).map(
            ([caregiverId, shiftIds]) => [caregiverId, Array.from(shiftIds)],
        ),
    );
}

/**
 * Unit-keyed variant of `buildCaregiverShiftIdsFromSelections`.
 *
 * Each display unit declares the shift ids it represents (a single shift
 * for ungrouped units, every group member for group units). Each selected
 * caregiver in a unit is emitted against every shift in that unit's
 * `shiftIds`, producing a `caregiver_id → [shift_ids]` record.
 */
export function buildCaregiverShiftIdsFromUnitSelections({
    selectedCaregiverIdsByUnit,
    displayUnits,
    currentUnitId,
    currentCaregiverIds,
}: {
    readonly selectedCaregiverIdsByUnit: Readonly<
        Record<string, Array<string>>
    >;
    readonly displayUnits: ReadonlyArray<{
        readonly id: string;
        readonly shiftIds: ReadonlyArray<string>;
    }>;
    readonly currentUnitId: string;
    readonly currentCaregiverIds: Array<string>;
}): CaregiverShiftIds {
    const selectedShiftIdsByCaregiver = new Map<string, Set<string>>();
    const currentSelectionByUnit: Record<string, Array<string>> = {
        ...selectedCaregiverIdsByUnit,
        [currentUnitId]: normalizeSelectedCaregiverIds(currentCaregiverIds),
    };

    for (const unit of displayUnits) {
        const caregiverIds = currentSelectionByUnit[unit.id] ?? [];
        for (const caregiverId of caregiverIds) {
            const shiftIds =
                selectedShiftIdsByCaregiver.get(caregiverId) ?? new Set();
            for (const shiftId of unit.shiftIds) {
                shiftIds.add(shiftId);
            }
            selectedShiftIdsByCaregiver.set(caregiverId, shiftIds);
        }
    }

    return Object.fromEntries(
        Array.from(selectedShiftIdsByCaregiver.entries()).map(
            ([caregiverId, shiftIds]) => [caregiverId, Array.from(shiftIds)],
        ),
    );
}

export function getPartialGroupSelectionWarnings({
    caregiverShiftIds,
    shifts,
    shiftGroups,
}: {
    readonly caregiverShiftIds: CaregiverShiftIds;
    readonly shifts: ReadonlyArray<ShiftForSelectionOrdering>;
    readonly shiftGroups?: ReadonlyArray<ShiftGroupForSelectionWarning>;
}): Array<PartialGroupSelectionWarning> {
    const normalizedShiftGroups = normalizeShiftGroupsForSelectionWarnings(
        shiftGroups,
        shifts,
    );

    if (normalizedShiftGroups.length === 0) {
        return [];
    }

    return Object.entries(caregiverShiftIds).flatMap(
        ([caregiverId, selectedShiftIds]) => {
            const selectedShiftIdSet = new Set(selectedShiftIds);

            return normalizedShiftGroups.flatMap((shiftGroup) => {
                const selectedGroupShiftIds = shiftGroup.shiftIds.filter(
                    (shiftId) => selectedShiftIdSet.has(shiftId),
                );
                if (
                    selectedGroupShiftIds.length === 0 ||
                    selectedGroupShiftIds.length === shiftGroup.shiftIds.length
                ) {
                    return [];
                }

                const missingShiftIds = shiftGroup.shiftIds.filter(
                    (shiftId) => !selectedShiftIdSet.has(shiftId),
                );

                return [
                    {
                        caregiverId,
                        groupId: shiftGroup.id,
                        groupIndex: shiftGroup.groupIndex,
                        selectedShiftIds: selectedGroupShiftIds,
                        missingShiftIds,
                    },
                ];
            });
        },
    );
}

export function expandCaregiverShiftIdsToWholeGroups({
    caregiverShiftIds,
    shifts,
    shiftGroups,
}: {
    readonly caregiverShiftIds: CaregiverShiftIds;
    readonly shifts: ReadonlyArray<ShiftForSelectionOrdering>;
    readonly shiftGroups?: ReadonlyArray<ShiftGroupForSelectionWarning>;
}): CaregiverShiftIds {
    const normalizedShiftGroups = normalizeShiftGroupsForSelectionWarnings(
        shiftGroups,
        shifts,
    );

    if (normalizedShiftGroups.length === 0) {
        return caregiverShiftIds;
    }

    const expandedShiftIdsByCaregiver = new Map<string, Set<string>>();
    for (const [caregiverId, shiftIds] of Object.entries(caregiverShiftIds)) {
        expandedShiftIdsByCaregiver.set(caregiverId, new Set(shiftIds));
    }

    for (const shiftGroup of normalizedShiftGroups) {
        for (const selectedShiftIds of expandedShiftIdsByCaregiver.values()) {
            const selectedGroupShiftCount = shiftGroup.shiftIds.filter(
                (shiftId) => selectedShiftIds.has(shiftId),
            ).length;
            if (
                selectedGroupShiftCount === 0 ||
                selectedGroupShiftCount === shiftGroup.shiftIds.length
            ) {
                continue;
            }

            for (const shiftId of shiftGroup.shiftIds) {
                selectedShiftIds.add(shiftId);
            }
        }
    }

    return Object.fromEntries(
        Array.from(expandedShiftIdsByCaregiver.entries())
            .sort(([leftCaregiverId], [rightCaregiverId]) =>
                leftCaregiverId.localeCompare(rightCaregiverId),
            )
            .map(([caregiverId, shiftIds]) => [
                caregiverId,
                orderKnownShiftIds(Array.from(shiftIds), shifts),
            ]),
    );
}
